import React from 'react';

import type { DefinitionStamp } from '../api/integrity';
import { UTC_ZONE, formatShortDateTime } from '../time/zonedTime';

/**
 * ADM-03-FR-09 — the window a metric was computed over, in words. Instants are
 * shown in UTC, the only reporting zone defined, and say so.
 */
export function windowLabel(window: DefinitionStamp['window']): string {
  const at = (iso: string) => `${formatShortDateTime(iso, UTC_ZONE)} UTC`;
  switch (window.kind) {
    case 'ROLLING_DAYS':
      return `últimos ${window.days ?? '?'} días hasta ${at(window.to)}`;
    case 'TERMINATION_MONTH':
      return window.from
        ? `ciclos terminados del ${at(window.from)} al ${at(window.to)}`
        : `ciclos terminados hasta ${at(window.to)}`;
    case 'COHORT_TO_DATE':
    default:
      return `acumulado hasta ${at(window.to)}`;
  }
}

/** "ADM13.ATTENDANCE v1 · acumulado hasta …" — the stamp in one compact line. */
export function definitionLabel(stamp: DefinitionStamp): string {
  return `${stamp.id} v${stamp.version} · ${windowLabel(stamp.window)}`;
}

/**
 * The definition stamp beside a metric: which rule, which version, which
 * window. Small and muted; it is there so two readers a month apart can tell
 * whether a number moved because the class changed or because the rule did.
 */
export const DefinitionTag: React.FC<{ stamp?: DefinitionStamp }> = ({ stamp }) =>
  stamp ? (
    <span
      className="mut"
      data-testid={`definition-${stamp.id}`}
      style={{ display: 'block', fontSize: 11, fontWeight: 400, lineHeight: 1.3 }}
    >
      {definitionLabel(stamp)}
    </span>
  ) : null;
